import { useCallback, useMemo } from "react";
import { ConfigurablePage, PageConfigurationSchema } from "./pageConfiguration.types";
import { usePageConfiguration } from "./PageConfigurationProvider";

/**
 * @param page
 * @param defaultState
 * @see PageConfigurationSchema
 */
export const usePageConfigurationState = <TPage extends ConfigurablePage>(
	page: TPage,
	defaultState: PageConfigurationSchema[TPage]
) => {
	// access global persisted state + updater
	const { state, setPageConfiguration } = usePageConfiguration();

	// extract stored config for current page
	const stored = state[page];

	// build final config used by UI
	// default is always base, stored overrides it
	const config = useMemo(() => {
		const base = defaultState;

		// if nothing stored, fallback to defaults
		if (!stored) return base;

		// merge persisted config over defaults
		return {
			...base,
			...stored,
		};
	}, [stored, defaultState]);

	// update only partial fields of config
	// avoids overwriting full page state
	const setConfig = useCallback(
		(partial: Partial<PageConfigurationSchema[TPage]>) => {
			setPageConfiguration(page, partial);
		},
		[page, setPageConfiguration]
	);

	// expose final config + updater
	return [config, setConfig] as const;
};
